"use client"

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react'

interface OnboardingStep {
  id: string
  title: string
  description: string
  content?: React.ReactNode
  media?: {
    type: 'video' | 'image' | 'gif'
    src: string
    alt?: string
  }
  completed?: boolean
}

interface OnboardingContextType {
  // Welcome flow state
  currentWelcomeStep: number
  welcomeSteps: OnboardingStep[]
  setWelcomeSteps: (steps: OnboardingStep[]) => void
  nextWelcomeStep: () => void
  previousWelcomeStep: () => void
  completeWelcomeStep: (stepId: string) => void
  isWelcomeCompleted: boolean
  
  // Tour state  
  showTour: boolean
  startTour: () => void
  completeTour: () => void
  isTourCompleted: boolean
  
  // Overall onboarding state
  isOnboardingCompleted: boolean
  completeOnboarding: () => void
  resetOnboarding: () => void
  currentPhase: 'welcome' | 'tour' | 'completed'
  
  // User preferences
  skipWelcome: () => void
  skipTour: () => void
  userPreferences: {
    skipWelcomeOnReturn: boolean
    skipTourOnReturn: boolean
    hasSeenOnboarding: boolean
  }
  updateUserPreferences: (prefs: Partial<OnboardingContextType['userPreferences']>) => void
}

const OnboardingContext = createContext<OnboardingContextType | null>(null)

interface OnboardingProviderProps {
  children: React.ReactNode
  onComplete?: () => void
  autoStart?: boolean
  persistState?: boolean
}

const STORAGE_KEY = 'socialloop-onboarding-state'

const defaultWelcomeSteps: OnboardingStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to SocialLoop!',
    description: 'Your AI-powered event networking platform that creates meaningful connections.',
    completed: false
  },
  {
    id: 'ai-matching',
    title: 'AI-Powered Matching',
    description: 'Our intelligent system matches you with like-minded attendees based on interests, goals, and networking preferences.',
    completed: false
  },
  {
    id: 'interactive-games',
    title: 'Interactive Networking Games',
    description: 'Break the ice with fun, engaging activities designed to spark conversations and build connections.',
    completed: false
  },
  {
    id: 'real-time-analytics',
    title: 'Real-Time Analytics',
    description: 'Track your networking success, view connection insights, and optimize your event experience.',
    completed: false
  },
  {
    id: 'ready',
    title: "You're All Set!",
    description: "Now let's take a quick tour of the interface to get you started.",
    completed: false
  }
]

export function OnboardingProvider({ 
  children, 
  onComplete,
  autoStart = true,
  persistState = true 
}: OnboardingProviderProps) {
  const [currentWelcomeStep, setCurrentWelcomeStep] = useState(1)
  const [welcomeSteps, setWelcomeSteps] = useState<OnboardingStep[]>(defaultWelcomeSteps)
  const [showTour, setShowTour] = useState(false)
  const [isWelcomeCompleted, setIsWelcomeCompleted] = useState(false)
  const [isTourCompleted, setIsTourCompleted] = useState(false)
  const [userPreferences, setUserPreferences] = useState({
    skipWelcomeOnReturn: false,
    skipTourOnReturn: false,
    hasSeenOnboarding: false
  })

  // Load persisted state
  useEffect(() => {
    if (persistState && typeof window !== 'undefined') {
      const saved = localStorage.getItem(STORAGE_KEY)
      if (saved) {
        try {
          const state = JSON.parse(saved)
          setCurrentWelcomeStep(state.currentWelcomeStep || 1)
          setIsWelcomeCompleted(state.isWelcomeCompleted || false)
          setIsTourCompleted(state.isTourCompleted || false)
          setUserPreferences(prev => ({ ...prev, ...state.userPreferences }))
          if (state.welcomeSteps) {
            setWelcomeSteps(state.welcomeSteps)
          }
        } catch (error) {
          console.warn('Failed to load onboarding state:', error)
        }
      }
    }
  }, [persistState])

  // Save state to localStorage
  const saveState = useCallback(() => {
    if (persistState && typeof window !== 'undefined') {
      const state = {
        currentWelcomeStep,
        isWelcomeCompleted,
        isTourCompleted,
        userPreferences,
        welcomeSteps
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
    }
  }, [currentWelcomeStep, isWelcomeCompleted, isTourCompleted, userPreferences, welcomeSteps, persistState])

  useEffect(() => {
    saveState()
  }, [saveState])

  const nextWelcomeStep = useCallback(() => {
    if (currentWelcomeStep < welcomeSteps.length) {
      setCurrentWelcomeStep(prev => prev + 1)
    } else {
      setIsWelcomeCompleted(true)
      if (autoStart) {
        setShowTour(true)
      }
    }
  }, [currentWelcomeStep, welcomeSteps.length, autoStart])

  const previousWelcomeStep = useCallback(() => {
    if (currentWelcomeStep > 1) {
      setCurrentWelcomeStep(prev => prev - 1)
    }
  }, [currentWelcomeStep])

  const completeWelcomeStep = useCallback((stepId: string) => {
    setWelcomeSteps(prev => 
      prev.map(step => 
        step.id === stepId ? { ...step, completed: true } : step
      )
    )
  }, [])

  const startTour = useCallback(() => {
    setShowTour(true)
  }, [])

  const completeTour = useCallback(() => {
    setIsTourCompleted(true)
    setShowTour(false)
    onComplete?.()
  }, [onComplete])

  const skipWelcome = useCallback(() => {
    setIsWelcomeCompleted(true)
    setUserPreferences(prev => ({ ...prev, skipWelcomeOnReturn: true }))
    if (autoStart) {
      setShowTour(true)
    }
  }, [autoStart])

  const skipTour = useCallback(() => {
    setIsTourCompleted(true)
    setShowTour(false)
    setUserPreferences(prev => ({ ...prev, skipTourOnReturn: true }))
    onComplete?.()
  }, [onComplete])

  const completeOnboarding = useCallback(() => {
    setIsWelcomeCompleted(true)
    setIsTourCompleted(true)
    setShowTour(false)
    setUserPreferences(prev => ({ ...prev, hasSeenOnboarding: true }))
    onComplete?.()
  }, [onComplete])

  const resetOnboarding = useCallback(() => {
    setCurrentWelcomeStep(1)
    setIsWelcomeCompleted(false)
    setIsTourCompleted(false)
    setShowTour(false)
    setWelcomeSteps(defaultWelcomeSteps)
    setUserPreferences({
      skipWelcomeOnReturn: false,
      skipTourOnReturn: false,
      hasSeenOnboarding: false
    })
    if (persistState && typeof window !== 'undefined') {
      localStorage.removeItem(STORAGE_KEY)
    }
  }, [persistState])

  const updateUserPreferences = useCallback((prefs: Partial<OnboardingContextType['userPreferences']>) => {
    setUserPreferences(prev => ({ ...prev, ...prefs }))
  }, [])

  const currentPhase: 'welcome' | 'tour' | 'completed' = 
    !isWelcomeCompleted ? 'welcome' :
    !isTourCompleted ? 'tour' : 'completed'

  const isOnboardingCompleted = isWelcomeCompleted && isTourCompleted

  const contextValue: OnboardingContextType = {
    currentWelcomeStep,
    welcomeSteps,
    setWelcomeSteps,
    nextWelcomeStep,
    previousWelcomeStep,
    completeWelcomeStep,
    isWelcomeCompleted,
    showTour,
    startTour,
    completeTour,
    isTourCompleted,
    isOnboardingCompleted,
    completeOnboarding,
    resetOnboarding,
    currentPhase,
    skipWelcome,
    skipTour,
    userPreferences,
    updateUserPreferences
  }

  return (
    <OnboardingContext.Provider value={contextValue}>
      {children}
    </OnboardingContext.Provider>
  )
}

export function useOnboarding() {
  const context = useContext(OnboardingContext)
  if (!context) {
    throw new Error('useOnboarding must be used within an OnboardingProvider')
  }
  return context
} 